import React, { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import FrontLayout from "../../../components/layout-new-theme/FrontLayout";
import LoginModal from "../../../components/auth/LoginModal";
import useEmailVerification, { VERIFICATION_STATUS } from "./hooks/useEmailVerification";
import {
  BusinessVerified,
  IndividualVerified,
  VerificationFailed,
  VerifyingState,
} from "./_components/VerificationStates";

// Loading → result: the spinner fades out before the result rises in.
const stateSwap = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.2 } },
};

// Height of the current state's content, so the card can ease between the
// short loading/error views and the taller success view instead of snapping.
const useMeasuredHeight = () => {
  const [height, setHeight] = useState("auto");
  const [element, setElement] = useState(null);

  useEffect(() => {
    if (!element) return undefined;
    const observer = new ResizeObserver(() => setHeight(element.offsetHeight));
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);

  return [setElement, height];
};

/**
 * /emailverification/:token — the page opened from the "verify your email"
 * link. Shows a loading state while the token is checked, then the success
 * message for business or individual users, or the error.
 */
const EmailVerification = ({ match }) => {
  const { status, user } = useEmailVerification(match.params.token);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [measureContentRef, contentHeight] = useMeasuredHeight();
  const shouldReduceMotion = useReducedMotion();

  const renderCurrentState = () => {
    if (status === VERIFICATION_STATUS.VERIFYING) return <VerifyingState />;
    if (status === VERIFICATION_STATUS.FAILED) return <VerificationFailed />;
    return user?.role === "business" ? (
      <BusinessVerified />
    ) : (
      <IndividualVerified onOpenLogin={() => setIsLoginModalOpen(true)} />
    );
  };

  return (
    <FrontLayout>
      <div className="tw:!bg-[#FFFFFF] tw:sm:!bg-[#F6F6F6]">
        <div className="tw:mx-auto tw:flex tw:min-h-[60vh] tw:w-full tw:max-w-[760px] tw:items-center tw:px-4 tw:py-10 tw:sm:py-16">
          <motion.div
            className="tw:w-full tw:bg-white tw:px-2 tw:py-4 tw:sm:rounded-lg tw:sm:px-12 tw:sm:py-12 tw:sm:shadow-[0_8px_30px_-12px_rgba(40,40,40,0.18)]"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              className="tw:overflow-hidden"
              animate={{ height: contentHeight }}
              transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              <div ref={measureContentRef}>
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div key={status} variants={stateSwap} initial="hidden" animate="visible" exit="exit">
                    {renderCurrentState()}
                  </motion.div>
                </AnimatePresence>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <LoginModal show={isLoginModalOpen} handleCloseModal={() => setIsLoginModalOpen(false)} />
    </FrontLayout>
  );
};

export default EmailVerification;
